import { useRef } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router';
import { ErrorBoundary } from '../app/ErrorBoundary';
import { UserMenu, useSession } from '../auth';
import { ConversationListPane } from '../features/conversations';
import { ConnectionBanner, SoundToggle, useConnectionNotice } from '../features/realtime';
import styles from './InboxLayout.module.css';
import { useNarrowFocus } from './use-narrow-focus';

/** Alvo do link "Ir para o conteúdo". */
const MAIN_ID = 'conteudo';

/**
 * Casca da caixa de entrada: conversas | conteúdo da rota (chat). O painel do contato (T25) entra
 * como terceira coluna. Em telas estreitas aparece uma coluna por vez: a lista quando nenhuma
 * conversa está aberta, o chat quando há uma.
 */
export function InboxLayout() {
  const session = useSession();
  const { pathname } = useLocation();
  const conversationId = useMatch('/c/:conversationId/*')?.params.conversationId ?? null;
  const open = conversationId !== null;
  const notice = useConnectionNotice();
  const listRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  useNarrowFocus(conversationId, mainRef, listRef);
  if (session.status !== 'authenticated') return null; // RequireAuth já garante; isto estreita o tipo

  return (
    <div className={styles.shell} data-conversation-open={open} data-banner={notice !== null}>
      {/* Primeiro item focável: a lista tem até dezenas de links antes do conteúdo. Sem `#conteudo`
          na URL (o roteador a trataria como navegação): só move o foco. */}
      <a
        href={`#${MAIN_ID}`}
        className={styles.skipLink}
        onClick={(event) => {
          event.preventDefault();
          mainRef.current?.focus();
        }}
      >
        Ir para o conteúdo
      </a>
      <ConnectionBanner notice={notice} />
      <aside ref={listRef} className={styles.list} aria-label="Conversas">
        <header className={styles.listHeader}>
          <h1 className={styles.title}>Conversas</h1>
          <UserMenu />
        </header>
        <div className={styles.tools}>
          <SoundToggle />
          {session.user.role === 'admin' && (
            <Link to="/admin" className={styles.adminLink}>
              Administração
            </Link>
          )}
        </div>
        <ConversationListPane role={session.user.role} />
      </aside>
      {/* `tabIndex={-1}`: recebe foco por código (link de salto e layout estreito), fora do Tab. */}
      <main ref={mainRef} id={MAIN_ID} tabIndex={-1} className={styles.main}>
        {/* Um defeito do painel não derruba a lista nem o "Sair"; outra conversa o reinicia. */}
        <ErrorBoundary scope="panel" resetKey={pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
